"use client";

export interface ContextContactFallback {
  id: string;
  company_id?: string | null;
  company_name?: string | null;
}

export interface ModalContactData {
  id: string;
  company_id?: string;
  company_name?: string;
}

export interface ModalCompanyData {
  id: string;
  name: string;
}

/** Modal open payloads may omit id or name; we narrow to ModalCompanyData when both are present */
export type ModalCompanyModalInput = ModalCompanyData | { id?: string; name?: string };

function modalCompanyToData(
  input: ModalCompanyModalInput | undefined
): ModalCompanyData | null {
  if (!input) return null;
  if (typeof input.id === "string" && typeof input.name === "string") {
    return { id: input.id, name: input.name };
  }
  return null;
}

export interface ResolvedCompanyModalContext {
  contactData: ModalContactData | null;
  companyId: string | null;
  companyName: string | null;
  companyData: ModalCompanyData | null;
}

interface ResolveCompanyModalContextInput {
  modalContact?: ModalContactData;
  modalCompany?: ModalCompanyModalInput;
  contextContact?: ContextContactFallback;
  detailItem?: unknown;
  detailEntityName?: string;
}

export function resolveCompanyModalContext({
  modalContact,
  modalCompany,
  contextContact,
  detailItem,
  detailEntityName,
}: ResolveCompanyModalContextInput): ResolvedCompanyModalContext {
  let contactData: ModalContactData | null = modalContact ?? null;
  let companyData: ModalCompanyData | null = modalCompanyToData(modalCompany);
  let companyName: string | null =
    companyData?.name ??
    (typeof modalCompany?.name === "string" ? modalCompany.name : null);
  let companyId: string | null =
    companyData?.id ??
    (typeof modalCompany?.id === "string" ? modalCompany.id : null) ??
    modalContact?.company_id ??
    null;

  if (!contactData && contextContact) {
    contactData = {
      id: contextContact.id,
      company_id: contextContact.company_id ?? undefined,
      company_name: contextContact.company_name ?? undefined,
    };
    if (!companyId && contextContact.company_id) {
      companyId = contextContact.company_id;
    }
    if (!companyName && contextContact.company_name) {
      companyName = contextContact.company_name;
    }
  }

  if (!detailItem || typeof detailItem !== "object") {
    return { contactData, companyId, companyName, companyData };
  }

  const item = detailItem as {
    id?: string;
    name?: string;
    company_id?: string;
    company_name?: string;
  };

  // Contact detail/list item with linked company
  if (!contactData && typeof item.company_id === "string") {
    contactData = {
      id: typeof item.id === "string" ? item.id : "",
      company_id: item.company_id,
      company_name: typeof item.company_name === "string" ? item.company_name : undefined,
    };
    if (!companyId) companyId = item.company_id;
    if (!companyName && typeof item.company_name === "string") {
      companyName = item.company_name;
    }
  }

  // Company detail/list item
  if (
    !companyData &&
    detailEntityName === "companies" &&
    typeof item.id === "string" &&
    typeof item.name === "string"
  ) {
    companyData = { id: item.id, name: item.name };
    if (!companyId) companyId = item.id;
    if (!companyName) companyName = item.name;
  }

  return { contactData, companyId, companyName, companyData };
}
